import Bugsnag from "@bugsnag/js";
import { useQueryClient } from "@tanstack/react-query";
import _get from "lodash/get";
import _isNil from "lodash/isNil";
import _mergeWith from "lodash/mergeWith";
import _uniqBy from "lodash/uniqBy";
import { useRouter } from "next/router";
import QueryString, { ParsedQs } from "qs";
import qs from "qs";

import { TabbedFormTabs } from "components/form/tabbedForm/TabbedForm";
import {
  CanvasDocumentData,
  CheckboxGroupFieldSlice,
  ChooseImageFieldSlice,
  DropdownSelectFieldSlice,
  FormDocument,
  FormDocumentDataSlicesSlice,
  MultiLineTextFieldSlice,
  MultiTextFieldSlice,
  RadioGroupFieldSlice,
  SingleLineTextFieldSlice,
  UploadPhotosFieldSlice,
} from "prismicio-types";
import {
  useGetSchema,
  getToc,
  productKeys,
  useGetProduct,
  useGetCustomisation,
} from "queries/legacy/product-queries";
import {
  CharacterFieldFindByKeyResponse,
  CharacterSchemaField,
  CharactersSchemaField,
  HTMLInputRules,
  Rules,
  SchemaComponent,
  SchemaField,
  SchemaFieldFindByKeyResponse,
  Upsell,
  Upsells,
} from "types/schema";

import {
  getActiveErrors,
  isSchemaValidationErrors,
  MISSING_CUSTOMISATION_ERROR,
  sanitizeCustomisation,
  SSR_CONFIGURATION_ERROR,
} from "./error-utils";
import { useInterpolate } from "./prismic-utils";

/**
 * Type guard for schema fields.
 *
 * @param field - The field to check
 * @param componentType - Component type to check against
 * @returns boolean with inferred type
 *
 * TODO: use zod to ensure metas are valid?
 */
export function isFieldType<T extends SchemaField>(
  field: SchemaField,
  componentType: SchemaComponent
): field is T {
  return !!field && field.metas[0].canvasComponent === componentType;
}

/**
 * Convert array of Schema 'rules' into HTML attributes
 */
export function getInputPropsFromSchemaRules(rules?: Rules): HTMLInputRules {
  const inputProps: HTMLInputRules = {};

  const getLimit = (limit: number) => {
    // if the field requires an integer value the Joi rule will be the bounds
    // of the integer rather than the string length, so infer a length from the value
    const isInteger = !!rules?.find((rule) => rule.name === "integer");
    return isInteger ? String(limit).length : limit;
  };

  rules?.forEach((rule) => {
    switch (rule.name) {
      case "min":
        inputProps.minLength = getLimit(rule.args.limit);
        break;
      case "max":
        inputProps.maxLength = getLimit(rule.args.limit);
        break;
      // case 'pattern':
      //   inputProps.pattern = (rule as RulePattern).args.regex;
      // ^ All patterns are wrong at the moment – remove for now to avoid breaking forms
    }
  });

  return inputProps;
}

export function getPrismicKeyFromErrorType(
  errorType: string
): keyof CanvasDocumentData {
  switch (errorType) {
    case "any.required":
      return "form_field_required_error_message";
    case "any.only":
      return "form_field_selection_error_message";
    case "string.empty":
      return "form_field_required_error_message";
    case "string.min":
      return "form_field_min_length_error_message";
    case "string.max":
      return "form_field_max_length_error_message";
    case "string.pattern.base":
      return "form_field_pattern_error_message";
    default:
      return "form_field_unknown_error_message";
  }
}

export function useGetUpsellForField(fieldKey?: string): Upsells | undefined {
  const { data: product } = useGetProduct();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  if (!fieldKey) {
    return;
  }

  const upsell = product.upsells.find((u) => u.slug === fieldKey);

  if (!upsell || upsell.variants.every((u) => u.price.amount === "0.0")) {
    return;
  }

  return upsell.variants.map(
    (variant) =>
      ({
        id: variant.sku.split(":").pop() as string,
        // ^ TODO: is there a better field for us to use?
        price: variant.price,
        wasPrice: variant.price?.was_price_formatted,
      }) as Upsell
  );
}

/**
 * Merge complex arrays, but replace string arrays to support MultiText
 * e.g. `['a', 'b'] –> ['c']` should return `['c']`, *not* `['c', 'b']`
 */
function replaceDefinedStringArrays(objValue: any, srcValue: any) {
  if (
    Array.isArray(srcValue) &&
    (srcValue.length === 0 || typeof srcValue[0] === "string")
  ) {
    return srcValue;
  }

  return undefined;
}

/**
 * Encapsulate logic for upsell ID values - special behaviour for photo upsells
 *
 * @param slug upsell identifier
 * @param data current customisation object
 */
function getUpsellId(key: string, value: unknown) {
  // TODO we should use findByKey here (or within combineCustomisation) to more
  // concretely identify the field as a photo upload by it's "canvasComponent" value
  // but useFindByKey ultimately calls combineCustomisation again and we end
  // up with a circular logic headache
  const isPhotoUpload = !!key.match(/_photo_upload$/);

  if (isPhotoUpload) {
    // account for param value being an empty string if photo has been removed
    const presence = value !== "" ? "true" : "false";
    return key + ":" + presence;
  }

  return key + ":" + value;
}

/**
 * Validate only the active fields in a form.
 *
 * Split out into this hook for reuse across Drawers, In-Page Edits and Creation Flow.
 */
export function usePartialTocValidation() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();

  const combineCustomisation = useGetCombinedCustomisation();

  async function validate(data: ParsedQs) {
    if (!product) {
      throw SSR_CONFIGURATION_ERROR;
    }

    if (!customisation) {
      throw MISSING_CUSTOMISATION_ERROR;
    }

    // Merge customisation so e.g. single character edits don't overwrite other characters
    const combinedCustomisation = combineCustomisation({
      oldData: customisation,
      newData: data,
    });

    // Validate form against toc
    try {
      // If query succeeds, it'll cache for the redirect
      // If it fails we can handle displaying the form errors
      await queryClient.fetchQuery(
        productKeys.toc(product.museSlug, combinedCustomisation),
        () => getToc(product.museSlug, combinedCustomisation)
      );

      return { combinedCustomisation };
    } catch (error) {
      if (isSchemaValidationErrors(error)) {
        // Intercept the catch to return the active errors
        const errorData = error.response.data;
        const activeErrors = getActiveErrors(errorData, data);

        if (activeErrors.length > 0) {
          return { errors: activeErrors };
        }

        return { combinedCustomisation };
      }

      const loggableError =
        error instanceof Error
          ? error
          : new Error("Unexpected non-validation SchemaFormModal Error");

      Bugsnag.notify(loggableError, (event) => {
        event.severity = "error";
        event.unhandled = false;
        event.addMetadata(
          "Customisation",
          customisation ? sanitizeCustomisation(customisation) : {}
        );
        event.addMetadata("Query", router.query);
      });

      // Re-throw if something unexpected happens
      throw error;
    }
  }

  return validate;
}

export function useFindByKey() {
  const { data: schema } = useGetSchema();

  /**
   * Look up a key in the Schema, returning the Schema entry & how it's represented in the URL.
   *
   * The key can be a path like `a[0].b.c` for deep access.
   *
   * Similarly to the `micromustache` helper functions, we use the first entry in the path
   * to determine in which context to search.
   */
  function findByKey(
    key: string
  ): SchemaFieldFindByKeyResponse | CharacterFieldFindByKeyResponse {
    if (!schema) {
      return null;
    }

    let response = null;

    const keyPaths = key.split(".");

    /**
     * Avatars, provided in the format `avatars.[role/type].[field?]`
     *
     * We don't use `characters` in these keys as older books use that key in a non-avatars context
     */
    if (keyPaths[0] === "avatars") {
      const [, roleOrType, field] = keyPaths;

      // Just `avatars` means we only care that they're defined at all
      if (!roleOrType) {
        response = {
          urlKey: "characters",
        };
      }

      // Get character types from outer array (e.g. adult, child, dog)
      (schema.keys.characters as CharactersSchemaField).ordered?.forEach(
        (characterType, i) => {
          // If we're looking for a `type`
          if (characterType.keys.type.allow[0] === roleOrType) {
            if (field === "count") {
              // Return the sibling entry if we're looking for `count` (e.g. how many kids in the book)
              response = {
                ...characterType.keys[field],
                characterTypeIndex: i,
                urlKey: `characters[${i}].count`,
              };
            } else if (!field) {
              // Otherwise return all entries of this type
              const characters: Record<string, CharacterSchemaField> = {};

              characterType.keys.customisations.ordered?.forEach(
                (character) => (characters[character.metas[0].role] = character)
              );

              response = {
                ...characters,
                characterTypeIndex: i,
                urlKey: `characters[${i}]`,
              };
            }
          }

          // Get individual characters from inner array (e.g. youngest child, oldest child)
          characterType.keys.customisations.ordered?.forEach((character, j) => {
            // If the `role` matches the key path...
            if (character.metas[0].role === roleOrType) {
              if (field) {
                if (field === "type") {
                  // ...and we want to know what `type` this role is:
                  response = {
                    ...characterType.keys.type,
                    characterTypeIndex: i,
                    urlKey: `characters[${i}].type`,
                  };
                } else if (character.keys[field]) {
                  // ...or the field exists in that character:
                  response = {
                    ...character.keys[field],
                    urlKey: `characters[${i}].customisations[${j}].${field}`,
                  };
                }
              } else {
                // Otherwise, return the character root and indexes
                response = {
                  ...character,
                  characterTypeIndex: i,
                  characterIndex: j,
                  urlKey: `characters[${i}].customisations[${j}]`,
                } as CharacterFieldFindByKeyResponse;
              }
            }
          });
        }
      );
    }
    /**
     * Support old `characters` as well
     */
    if (keyPaths[0] === "characters") {
      const [, characterIndex, field] = keyPaths;

      if ("items" in schema.keys.characters) {
        response = {
          ...(schema.keys.characters.items as any)[characterIndex].keys[field],
          urlKey: `characters[${characterIndex}].${field}`,
        };
      }
    } else if (_get(schema.keys, key)) {
      response = {
        ..._get(schema.keys, key),
        urlKey: key,
      };
    }

    return response;
  }

  return findByKey;
}

/**
 * FormDocuments can optionally have an array of 'Tabs' to create a tabbed sub-form.
 *
 * Create a Slice Zone for each tab, including only the fields that exist in this product's schema.
 * This is so shared Forms like Avatars don't need to exclude e.g. eye colour
 */
export function useGetTabsFromFormDocument(formDocument: FormDocument) {
  const findByKey = useFindByKey();
  const interpolate = useInterpolate();

  const tabs: TabbedFormTabs = [];

  formDocument.data.tabs.forEach((tab) => {
    if (!tab.muse_schema_keys) {
      /**
       * Would like to throw a configuration error here, e.g. `Tab is missing "muse_schema_keys"`.
       *
       * But for whatever reason, Prismic adds an empty entry to repeatable zones by default,
       * making null `muse_schema_keys` happen a lot.
       */
      return;
    }

    const keys = tab.muse_schema_keys.split(",");
    const slices = formDocument.data.slices.filter((s) => {
      return (
        "muse_schema_key" in s.primary &&
        s.primary.muse_schema_key &&
        // Pick the slices in this tab
        keys.indexOf(s.primary.muse_schema_key) > -1 &&
        // But only if they are in the Schema
        // The name of this function is the same of a proprietaty testing library
        // function so we we need to disable the eslint rule
        // eslint-disable-next-line testing-library/await-async-query
        findByKey(s.primary.muse_schema_key)
      );
    });

    // Make sure empty tabs aren't included
    if (slices.length > 0) {
      tabs.push({
        slices,
        keys,
        title: interpolate(tab.title ?? `Tab ${tabs.length}`),
      });
    }
  });

  // Simplify falsy checks by returning `null` rather than `[]`
  if (tabs.length === 0) {
    return null;
  }

  return tabs;
}

/**
 * Manually creates a Prismic FormDocument using the entire schema.
 *
 * Use in a <SliceZone> to natively edit all of a book's fields.
 */
export function useGetGlobalFormFromSchema() {
  const { data: schema } = useGetSchema();

  const form: FormDocument & {
    data: { slices: FormDocumentDataSlicesSlice[] };
  } = {
    id: "",
    uid: null,
    url: null,
    type: "form",
    href: "",
    tags: [],
    first_publication_date: "0000-00-00T00:00:00+0000",
    last_publication_date: "0000-00-00T00:00:00+0000",
    slugs: [],
    linked_documents: [],
    lang: "en-us",
    alternate_languages: [],
    data: {
      tracking_id: null,
      title: null,
      subtitle: null,
      preview: {
        link_type: "Document",
      },
      tabs: [],
      slices: [],
    },
  };

  if (schema) {
    /**
     * For each `canvasComponent`, add the base Prismic fields necessary to render
     */
    Object.entries(schema.keys).forEach(([key, entry]) => {
      const sliceBase: Pick<
        FormDocumentDataSlicesSlice,
        "version" | "id" | "slice_label"
      > = {
        version: "",
        id: "",
        slice_label: null,
      };

      if (entry.metas?.[0].canvasComponent === "checkbox_group") {
        const slice: CheckboxGroupFieldSlice = {
          ...sliceBase,
          variation: "default",
          items: [],
          primary: {
            muse_schema_key: key,
            columns: 2,
            desktop_columns: 4,
            label: key,
          },
          slice_type: "checkbox_group_field",
        };
        form.data.slices.push(slice);
      } else if (entry.metas?.[0].canvasComponent === "choose_image") {
        const slice: ChooseImageFieldSlice = {
          ...sliceBase,
          variation: "default",
          items: [],
          primary: {
            muse_schema_key: key,
            columns: 2,
            desktop_columns: 4,
            label: key,
          },
          slice_type: "choose_image_field",
        };
        form.data.slices.push(slice);
      } else if (entry.metas?.[0].canvasComponent === "dropdown_select") {
        const slice: DropdownSelectFieldSlice = {
          ...sliceBase,
          variation: "default",
          items: [],
          primary: {
            muse_schema_key: key,
            label: key,
          },
          slice_type: "dropdown_select_field",
        };
        form.data.slices.push(slice);
      } else if (entry.metas?.[0].canvasComponent === "multi_line_text") {
        const slice: MultiLineTextFieldSlice = {
          ...sliceBase,
          variation: "default",
          items: [],
          primary: {
            muse_schema_key: key,
            label: key,
            placeholder: "",
          },
          slice_type: "multi_line_text_field",
        };
        form.data.slices.push(slice);
      } else if (entry.metas?.[0].canvasComponent === "multi_text") {
        const slice: MultiTextFieldSlice = {
          ...sliceBase,
          variation: "default",
          items: [],
          primary: {
            muse_schema_key: key,
            label: key,
            input_placeholder: "",
          },
          slice_type: "multi_text_field",
        };
        form.data.slices.push(slice);
      } else if (entry.metas?.[0].canvasComponent === "radio_group") {
        const slice: RadioGroupFieldSlice = {
          ...sliceBase,
          variation: entry.metas[0].freeEntryComponent
            ? "withFreeEntry"
            : ("default" as any),
          items: [],
          primary: {
            muse_schema_key: key,
            label: key,
            choose_image_columns: 2,
            choose_image_desktop_columns: 4,
            columns: 2,
            desktop_columns: 4,
            free_entry_label: "",
            free_entry_placeholder: "",
          },
          slice_type: "radio_group_field",
        };
        form.data.slices.push(slice);
      } else if (entry.metas?.[0].canvasComponent === "single_line_text") {
        const slice: SingleLineTextFieldSlice = {
          ...sliceBase,
          variation: "default",
          items: [],
          primary: {
            muse_schema_key: key,
            label: key,
            placeholder: "",
          },
          slice_type: "single_line_text_field",
        };
        form.data.slices.push(slice);
      } else if (entry.metas?.[0].canvasComponent === "upload_photos") {
        const slice: UploadPhotosFieldSlice = {
          ...sliceBase,
          variation: "default",
          items: [],
          primary: {
            muse_schema_key: key,
            label: key,
          },
          slice_type: "upload_photos_field",
        };
        form.data.slices.push(slice);
      }
    });
  }

  return form;
}

/** Split ratio expression like "2 / 1" or "2/1" to parts */
export function calculateAspectFromRatio(ratio: string) {
  const [w, h] = ratio.replace(/ /g, "").split("/");

  if (!w || !h || isNaN(Number(w)) || isNaN(Number(h))) {
    throw new Error(`Aspect ratio "${ratio}" is invalid`);
  }

  return Number(w) / Number(h);
}

export function useGetCombinedCustomisation() {
  const router = useRouter();

  const { data: product } = useGetProduct();

  /**
   * Merge provided data with existing customisations and set correct `components` entry for upsells.
   */
  function combineCustomisation({
    newData,
    oldData,
  }: {
    newData: ParsedQs;
    oldData: ParsedQs;
  }) {
    if (!product) {
      throw SSR_CONFIGURATION_ERROR;
    }

    // Merge all params, adding the router query
    const combinedCustomisation = _mergeWith(
      {},
      oldData,
      newData,
      qs.parse(qs.stringify(router.query)),
      // ^ router path (e.g. `marketplace`, `solidus_slug`) should override all
      replaceDefinedStringArrays
    ) as QueryString.ParsedQs;

    /**
     * Upsells must be listed in a `components` param or they won't get charged!
     * Must be an array of objects with (at least) the shape { id: "slug:value" }
     *
     * In edit mode the object will also contain the component's pricing:
     * (e.g. components[0][id]=cover_style:option-i&components[0][price_at_checkout][amount]=3.0&components[0][price_at_checkout][formatted]=$3.00 USD)
     */
    const components = (
      (combinedCustomisation.components || []) as ParsedQs[]
    ).reduce((all, component) => {
      if (typeof component === "string") {
        throw new Error("Component is unexpectedly a string");
      } else if (typeof component.id !== "string") {
        throw new Error("Component ID is unexpectedly not a string");
      }

      const idParts = component.id.split(":");
      const customisationValue = combinedCustomisation[idParts[0]];

      // Some upsells like giftwrap don't correspond to a personalisation.
      // We need to maintain the upsell even if it doesn't feature in the data.
      // _isNil allows customisations with empty string values through
      if (_isNil(customisationValue)) {
        return [...all, component];
      }

      // Otherwise update the upsell with the combined customisation
      return [
        ...all,
        {
          id: getUpsellId(idParts[0], customisationValue),
        },
      ];
    }, [] as ParsedQs[]);

    // Check for any new upsold fields added
    const newComponents = product.upsells
      .filter(({ slug }) => !!combinedCustomisation[slug])
      // ^ Ensure we have a value for this upsell first
      .map(({ slug }) => ({
        id: getUpsellId(slug, combinedCustomisation[slug]),
      }));

    return {
      ...combinedCustomisation,
      components: _uniqBy(
        [...components, ...newComponents],
        // Combine the old and new components together, ensuring no duplicates with the same ID occur
        (component) => (component.id as string).split(":")[0]
      ),
    };
  }

  return combineCustomisation;
}
